import { FunctionComponent as F } from "react";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from "@mui/material/Box";
import { MenuItem, Menu } from "@mui/material";
import { bindMenu, PopupState } from 'material-ui-popup-state/hooks'
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { IState } from "../../core/store/store";
import EditIcon from '@mui/icons-material/Edit';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { createFolderAsync } from "../../core/store/protocol/protocolFolder.thunks";
import { useAppDispatch } from "../../core/store/hooks";
import { duplicateAsync } from "../../core/store/protocol/protocol.thunks";


interface Props
{
  folderId: number,
  setMoveToFolderDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setNewFolderDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteFolderDialog: React.Dispatch<React.SetStateAction<boolean>>;
  popupState: PopupState,
  showRenameDialog: () => void,
  openNewProtocolDialog: (folderId?: number) => void,
}

const FolderListItemMenu: F<Props> = (props: Props) =>
{
  const appDispatch = useAppDispatch();
  const folder = useSelector((state: IState) => state.protocol.folders
    .find(f => f.folderId === props.folderId));
  const allProtocols = useSelector((state: IState) => state.protocol.protocols);
  const allFolders = useSelector((state: IState) => state.protocol.folders);
  const foldersInParent = allFolders.filter(f => f.parentFolderId === folder?.parentFolderId);

  const user = useSelector((state: IState) => state.user);
  const userProtocolsOutsideFolder = useSelector((state: IState) =>
    state.protocol.protocols.filter(p => p.folderId !== folder?.folderId && p.userId === user.userId));

  const handleNewProtocolClick = () =>
  {
    props.popupState.close();
    props.openNewProtocolDialog(props.folderId);
  }

  const handleDuplicate = async(
    _event?: React.MouseEvent<HTMLLIElement, MouseEvent>,
    recFolder = folder,
    parentFolderId = folder?.parentFolderId) =>
  {
    props.popupState.close();

    if(!recFolder)
      return;

    const copyName = recFolder?.name + (_event ? " Copy" : "");
    const newFolder = await appDispatch(createFolderAsync(copyName, parentFolderId));
    if(!newFolder)
      return;

    const protocols = allProtocols.filter(p => p.folderId === recFolder.folderId);
    const duplicateProtocols = Promise.all(protocols
      .map(async p => appDispatch(duplicateAsync(p.protocolId, newFolder.folderId, p.name))));

    const folders = allFolders.filter(f => f.parentFolderId === recFolder.folderId);
    await Promise.all(folders.map(f => handleDuplicate(undefined, f, newFolder.folderId)));
    await duplicateProtocols;
  }

  if(!folder)
    return <></>;

  return (
    <Menu {...bindMenu(props.popupState)} autoFocus={false} disableAutoFocusItem={true}>

      <MenuItem onClick={handleNewProtocolClick}>
        <ListItemIcon>
          <NoteAddIcon fontSize='small'/>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>New protocol</ListItemText>
      </MenuItem>

      <MenuItem onClick={() => { props.popupState.close(); props.setNewFolderDialog(true); }}>
        <ListItemIcon>
          <CreateNewFolderIcon fontSize='small'/>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>New folder</ListItemText>
      </MenuItem>

      {(folder.parentFolderId || (foldersInParent.length > 1)) ?
        <MenuItem onClick={() => { props.popupState.close(); props.setMoveToFolderDialog(true) }}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize='small'/>
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Move to folder...</ListItemText>
        </MenuItem> :
        <Box/>}

      {user.premium ?
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <FileCopyIcon fontSize='small'/>
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Duplicate</ListItemText>
        </MenuItem>  :
        <Box/>}

      <MenuItem onClick={() => { props.popupState.close(); props.showRenameDialog(); }}>
        <ListItemIcon>
          <EditIcon fontSize='small'/>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Rename</ListItemText>
      </MenuItem>

      {(userProtocolsOutsideFolder.length >= 1) ?
        <MenuItem onClick={() => { props.popupState.close(); props.setDeleteFolderDialog(true) }}>
          <ListItemIcon>
            <DeleteIcon fontSize='small'/>
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Delete</ListItemText>
        </MenuItem> :
        <Box/>}

    </Menu>);
}

export default FolderListItemMenu;

